import * as Equal from "effect/Equal";
import { estimateWorkflowRunCardHeight } from "./workflowRunView";
import { MCP_APP_CARD_ESTIMATED_PX } from "./mcpAppClient";
import {
  formatAwaitedTasks,
  formatSoleAwaitedWorkflow,
  type ModelFallbackState,
  type ForkContextEntry,
  type McpAppEntry,
  type MovedInEntry,
  type SubagentResultEntry,
  type TimelineEntry,
  type WorkLogEntry,
} from "../../session-logic";
import { isUserWrittenMessage } from "@threadlines/shared/roomAgentRequests";
import { isTurnAside } from "@threadlines/shared/transcriptRevert";
import { agentPageFrameHeight } from "@threadlines/shared/agentPages";
import { type ChatMessage, type ProposedPlan, type TurnDiffSummary } from "../../types";
import { type RoomAgentNames, roomAgentKey, roomTurnOwners } from "../../rooms";
import {
  type OrchestrationAgentPage,
  type OrchestrationAgentRequest,
  type OrchestrationCommandRequest,
  type MessageAgentModel,
  type MessageId,
  type OrchestrationAwaitedBackgroundTask,
  type OrchestrationCheckBack,
  type SideTurnId,
  type ThreadParticipantId,
  type TurnId,
  type OrchestrationWorkflowRun,
} from "@threadlines/contracts";
import { stripCodexInlineVisualizationDirectives } from "../../lib/codexInlineVisualization";
import { threadMemoOfInput } from "../../lib/derivationMemo";
import { shallow } from "zustand/vanilla/shallow";
import { deriveDisplayedUserMessageState } from "../../lib/terminalContext";
import {
  activityStepFromWorkLogEntry,
  commandCheckKey,
  isSilentWorkLogEntry,
  liveActivityLabel,
  liveThoughtText,
  OPEN_TAIL_LINES,
  threadlinesRoomToolOf,
} from "./activitySteps";
import {
  breaksWithin,
  isSideRow,
  placeSideExchanges,
  planSideExchanges,
  sideAnswerLiveWork,
  sideExchangeBreaks,
  type SideAnswerView,
} from "./sideAnswers";
import { NO_LIVE_AGENT_TRACKER_KEYS, type LiveAgentTrackerKeys } from "./agentsPanel.logic";
import type { LiveCommandOutput } from "./backgroundRunOutput";
import {
  deriveTimelineRowsShared,
  NO_TIMELINE_ROWS_SHARED,
  type TimelineRowsShared,
} from "./timelineRowsShared";

/** What a finished turn's footer says under its last message. */
export interface TurnSummary {
  /** Time spent working, without the wait before a Retry or a resumed turn. */
  readonly workedMs: number | null;
  readonly editedFileCount: number;
  /** Latest result of each check the turn ran; a rerun replaces a failure. */
  readonly checks: { readonly passed: number; readonly failed: number } | null;
  /** The turn's agents. The working row carried them while the turn ran, and
   *  the footer takes its place, so they stay at the tail. */
  readonly trackerTurnIds: ReadonlyArray<TurnId>;
  readonly trackerAgentSpawnIds: ReadonlyArray<string>;
}

/**
 * Where a row sits on its agent's line. Each agent's stretch of the chat (its
 * notes and steps, down to its answer or its working line) hangs from its logo
 * and name on one thin line. `start` carries the logo and name with the line
 * running on below; `through` carries the line past, including side exchanges
 * the agent worked through; `end` bends the line into the answer or working
 * line; `single` is a stretch of one row: the logo and name, and no line.
 * A finished answer that opens with a page or a command card bends into it
 * (`end`), and the rest of the answer, more panels and the reply, sits `past`
 * the bend.
 */
export type AgentLinePlacement = "start" | "through" | "end" | "past" | "single";

export interface AgentLine {
  readonly placement: AgentLinePlacement;
  /** Whose words or work the row is: an added agent, or null for the
   *  thread's own. Inside a stretch, a side answer is its answerer's. */
  readonly agent: ThreadParticipantId | null;
  /** The turn the stretch belongs to, from the first of its rows that names
   *  one: the model that ran it names the thread's own agent. Null while none
   *  does (the live turn before the provider names it), and for side answers. */
  readonly turnId: TurnId | null;
  /** The row carries its agent's logo and name: the first row of a stretch,
   *  or of a side answer, or a message one agent wrote to another. */
  readonly heads: boolean;
  /** Another agent's side answer inside this stretch, set in under the
   *  question the stretch's agent or the user asked, on a line of its own
   *  one lane in: the row's place on that line. Null for the stretch's own
   *  rows, whose place is `placement`. */
  readonly nested: AgentLinePlacement | null;
  /** A hand-off's reply whose words are the answer right above it. */
  readonly echoesAnswer: boolean;
  /** The agent as it was when it wrote here (OrchestrationMessage.agentModels),
   *  which its name follows: the first stamp among a stretch's own messages,
   *  a side answer's, or the message's own for one agent writing to another.
   *  Null when nothing it wrote here carries one. */
  readonly asWritten: MessageAgentModel | null;
}

export interface TimelineRowPlacement {
  /** The row's place on its agent's line (see AgentLine). Absent for rows no
   *  agent's stretch holds: your messages, plans. */
  readonly agentLine?: AgentLine | undefined;
  /** Room above the row, which is how an answer keeps page space above the
   *  work before it. Decided by what the row is and what sits above it, so a
   *  turn ending moves nothing. */
  readonly padTop: boolean;
}

const UNPLACED: TimelineRowPlacement = { padTop: false };

/**
 * The user writing starts an exchange. A message one room agent wrote to
 * another (a hand-off, its reply) happens inside the exchange: it neither
 * splits the turn that wrote it nor ends the user's exchange. A message from
 * another thread (child threads: a parent's request, a child's answer) or
 * from an agent outside Threadlines starts a turn of its own, so it starts an
 * exchange the way the user's would, unless it was steered into the turn
 * already running (isTurnAside).
 */
const startsExchange = (
  message: Pick<
    ChatMessage,
    | "role"
    | "fromAgent"
    | "fromThread"
    | "fromOutside"
    | "turnId"
    | "sideTurnId"
    | "requestKind"
    | "requestOutcome"
  >,
) =>
  isUserWrittenMessage(message) ||
  (message.role === "user" &&
    (message.fromThread !== undefined || message.fromOutside !== undefined) &&
    !isTurnAside(message));

export type MessagesTimelineRow = TimelineRowPlacement &
  (
    | {
        kind: "work";
        id: string;
        createdAt: string;
        /** A side answer's steps (sideAnswers.ts); absent for the working
         *  agent's own. */
        sideTurnId?: SideTurnId | undefined;
        groupedEntries: WorkLogEntry[];
        /** Agent lifecycle entries this group swallowed. Never rendered and never
         *  counted, but kept so the group still exists on a turn that did nothing
         *  but delegate: the turn's agent tracker and its duration hang off it. */
        agentAnchorEntries: WorkLogEntry[];
        /** The turns this group shows the agent tracker for. A tracker summarizes
         *  a whole turn, so only the turn's first group carries it; a later group
         *  in the same turn would repeat the same bars and count. Empty means this
         *  group shows no tracker at all. */
        trackerTurnIds: TurnId[];
        /** Spawn call ids the tracker falls back to when the group has no turn to
         *  key on. A background agent keeps streaming after its spawning turn
         *  settles, and that activity arrives turnless — these ids let the tail
         *  group name an agent no turn claims. The selector ignores ids whose
         *  agent carries a turn attribution: that agent's tracker lives with the
         *  spawning turn's group, and repeating it here would double the "Agent
         *  working" row. Only populated when `trackerTurnIds` is empty. */
        trackerAgentSpawnIds: string[];
        isLive: boolean;
        liveStartedAt: string | null;
        /** True while the turn this group belongs to is still working. Groups in
         *  the active exchange keep their live-spine shape (frozen, no accent)
         *  instead of collapsing into a receipt mid-turn — the settle happens
         *  once, when the turn ends. */
        inActiveExchange: boolean;
        /** The agent has written since this stretch, or its exchange is over:
         *  the stretch reads as one line that opens into its steps. */
        folded: boolean;
      }
    | {
        kind: "message";
        id: string;
        createdAt: string;
        message: ChatMessage;
        /** A note from a finished turn that is not its last message: it fades so
         *  the turn's answer reads first. */
        settledNote: boolean;
        /** Set on a finished turn's last message, which carries the turn's footer. */
        turnSummary: TurnSummary | null;
        showAssistantCopyButton: boolean;
        assistantCopyStreaming: boolean;
        assistantTurnInProgress: boolean;
        assistantModelFallback?: ModelFallbackState | undefined;
        assistantTurnDiffSummary?: TurnDiffSummary | undefined;
        revertTurnCount?: number | undefined;
        /** A side answer to an independent review: the request, for its tag. */
        sideReview?: ChatMessage | undefined;
        /** A side answer posted below the work that went on after its
         *  question: the question, to link back to. */
        sideReplyTo?: ChatMessage | undefined;
      }
    | {
        kind: "proposed-plan";
        id: string;
        createdAt: string;
        proposedPlan: ProposedPlan;
      }
    | {
        kind: "subagent-result";
        id: string;
        createdAt: string;
        result: SubagentResultEntry;
      }
    | {
        /** A workflow run, where the call that launched it was (WorkflowRunCard). */
        kind: "workflow-run";
        id: string;
        createdAt: string;
        run: OrchestrationWorkflowRun;
      }
    | {
        /** An MCP app, under the step whose tool shipped it (McpAppCard). */
        kind: "mcp-app";
        id: string;
        createdAt: string;
        app: McpAppEntry;
      }
    | {
        kind: "fork-context";
        id: string;
        createdAt: string;
        forkContext: ForkContextEntry;
      }
    | {
        /** Where a thread that moved here starts being this computer's (MovedInTimelineRow). */
        kind: "moved-in";
        id: string;
        createdAt: string;
        movedIn: MovedInEntry;
      }
    | {
        /** An agent page as one turn left it (AgentPageRow). */
        kind: "page";
        id: string;
        createdAt: string;
        page: OrchestrationAgentPage;
        /** A later turn's version of a page an earlier row shows. */
        updated: boolean;
      }
    | {
        /** A command an agent asked the user to run (CommandRequestRow). */
        kind: "command-request";
        id: string;
        createdAt: string;
        request: OrchestrationCommandRequest;
      }
    | {
        /** When the thread's agents will come back by themselves, once the
         *  turn has settled (checkBacks.ts). */
        kind: "check-back";
        id: string;
        createdAt: string | null;
        checkBacks: ReadonlyArray<OrchestrationCheckBack>;
      }
    | {
        kind: "working";
        id: string;
        createdAt: string | null;
        label: string;
        /** What the agent is thinking right now, in its own summary's words. */
        thought: string | null;
        /** The command the agent is running right now, whose newest printed
         *  line the row shows. */
        command: LiveCommandOutput | null;
        /** Trackers this row took over from turns whose agents still work
         *  (takeLiveAgentTrackers); empty when none do. */
        trackerTurnIds: TurnId[];
        trackerAgentSpawnIds: string[];
      }
    | {
        /** A side answer's state line: answering (with Stop), or how it ended
         *  without a reply. See sideAnswers.ts. */
        kind: "side-status";
        id: string;
        createdAt: string;
        sideTurnId: SideTurnId;
        participantId: ThreadParticipantId | null;
        /** An independent review an agent asked for. */
        review: boolean;
        /** The question, or an agent's request with how it ended. */
        question: ChatMessage;
        /** The answer, once there is one: `answered-below` links to it. */
        answerMessageId: MessageId | null;
        /** `answered-below`: under a question whose answer was posted further
         *  down, where it was done. `answered`: its answer is right under it,
         *  opened by the reader to watch it. */
        state: "answering" | "stopping" | "failed" | "stopped" | "answered-below" | "answered";
        error: string | null;
        /** Answering while the working agent goes on: one line that opens to
         *  show the answer being written. */
        compact: boolean;
        expanded: boolean;
        /** The step the answer is on (sideAnswerLiveWork); null between
         *  steps, and while the working row names it instead. */
        liveStep: string | null;
      }
  );

export interface StableMessagesTimelineRowsState {
  byId: Map<string, MessagesTimelineRow>;
  result: MessagesTimelineRow[];
  /** What the rows share with every row, kept as stable as the rows. */
  shared: TimelineRowsShared;
}

export const NO_STABLE_TIMELINE_ROWS: StableMessagesTimelineRowsState = {
  byId: new Map(),
  result: [],
  shared: NO_TIMELINE_ROWS_SHARED,
};

export function resolveAssistantMessageCopyState({
  text,
  showCopyButton,
  streaming,
}: {
  text: string | null;
  showCopyButton: boolean;
  streaming: boolean;
}) {
  const copyText = text === null ? null : stripCodexInlineVisualizationDirectives(text);
  const hasText = copyText !== null && copyText.trim().length > 0;
  return {
    text: hasText ? copyText : null,
    visible: showCopyButton && hasText && !streaming,
  };
}

const UNKEYED_TURN_SIGNAL = "__unkeyed_turn__";

function turnSignalKey(turnId: TurnId | null | undefined): string {
  return turnId ?? UNKEYED_TURN_SIGNAL;
}

function isRunningCommandWorkEntry(entry: WorkLogEntry): boolean {
  return (
    entry.executionState === "running" &&
    (entry.requestKind === "command" ||
      entry.itemType === "command_execution" ||
      entry.command !== undefined)
  );
}

function isCommandSupersedingWorkEntry(entry: WorkLogEntry): boolean {
  return entry.tone === "thinking";
}

function inferSupersededRunningCommandEntryIds(
  timelineEntries: ReadonlyArray<TimelineEntry>,
): Set<string> {
  const supersededEntryIds = new Set<string>();
  const laterThinkingOrAssistantByTurn = new Set<string>();

  for (let index = timelineEntries.length - 1; index >= 0; index -= 1) {
    const timelineEntry = timelineEntries[index];
    if (!timelineEntry) {
      continue;
    }

    if (timelineEntry.kind === "message") {
      if (timelineEntry.message.role === "assistant") {
        laterThinkingOrAssistantByTurn.add(turnSignalKey(timelineEntry.message.turnId));
      }
      continue;
    }

    if (timelineEntry.kind !== "work") {
      continue;
    }

    const { entry } = timelineEntry;
    if (
      isRunningCommandWorkEntry(entry) &&
      laterThinkingOrAssistantByTurn.has(turnSignalKey(entry.turnId))
    ) {
      supersededEntryIds.add(entry.id);
    }

    if (isCommandSupersedingWorkEntry(entry)) {
      laterThinkingOrAssistantByTurn.add(turnSignalKey(entry.turnId));
    }
  }

  return supersededEntryIds;
}

function settleSupersededRunningCommandEntry(
  entry: WorkLogEntry,
  supersededRunningCommandEntryIds: ReadonlySet<string>,
): WorkLogEntry {
  if (!supersededRunningCommandEntryIds.has(entry.id)) {
    return entry;
  }
  return { ...entry, executionState: "completed" };
}

function deriveTerminalAssistantMessageIds(timelineEntries: ReadonlyArray<TimelineEntry>) {
  const lastAssistantMessageIdByResponseKey = new Map<string, string>();
  let nullTurnResponseIndex = 0;

  for (const timelineEntry of timelineEntries) {
    if (timelineEntry.kind !== "message") {
      continue;
    }
    const { message } = timelineEntry;
    if (message.role === "user") {
      nullTurnResponseIndex += 1;
      continue;
    }
    if (message.role !== "assistant") {
      continue;
    }

    const responseKey = message.turnId
      ? `turn:${message.turnId}`
      : `unkeyed:${nullTurnResponseIndex}`;
    lastAssistantMessageIdByResponseKey.set(responseKey, message.id);
  }

  return new Set(lastAssistantMessageIdByResponseKey.values());
}

function deriveModelFallbackByTurn(
  timelineEntries: ReadonlyArray<TimelineEntry>,
): ReadonlyMap<string, ModelFallbackState> {
  const fallbackByTurn = new Map<string, ModelFallbackState>();
  for (const timelineEntry of timelineEntries) {
    if (timelineEntry.kind !== "work" || !timelineEntry.entry.modelFallback) {
      continue;
    }
    fallbackByTurn.set(
      turnSignalKey(timelineEntry.entry.modelFallback.turnId),
      timelineEntry.entry.modelFallback,
    );
  }
  return fallbackByTurn;
}

/**
 * `deriveMessagesTimelineRows`, remembered per thread (threadMemoOfInput): the
 * list remounts with each chat it shows, so without this a chat opened again
 * would place every row again. Reused only when every input field is the same
 * value; a room's agent names count as the same when they name the same
 * agents the same way, since the list builds them afresh as it mounts.
 */
export const deriveRememberedMessagesTimelineRows = threadMemoOfInput(
  (input: Parameters<typeof deriveMessagesTimelineRows>[0]) => deriveMessagesTimelineRows(input),
  { roomAgentNames: (left, right) => shallow(left, right) },
);

export function deriveMessagesTimelineRows(input: {
  timelineEntries: ReadonlyArray<TimelineEntry>;
  isWorking: boolean;
  /** Agents still running or waiting, whichever turn spawned them. The working
   *  anchor stays up for them after the turn settles, so a background agent's
   *  live line keeps one home at the tail instead of vanishing. */
  liveAgentCount?: number | undefined;
  /** What a turn's tracker can name those agents by (liveAgentTrackerSignature). */
  liveAgentTrackerKeys?: LiveAgentTrackerKeys | undefined;
  /** The turn settled but provider background work will wake it again. */
  isWaitingOnBackgroundTasks?: boolean | undefined;
  /** What that work is, when the server described it (describedAwaitedTasks). */
  awaitedTasks?: ReadonlyArray<OrchestrationAwaitedBackgroundTask> | null | undefined;
  /** Times the thread's agents set themselves to come back (Thread.checkBacks). */
  checkBacks?: ReadonlyArray<OrchestrationCheckBack> | undefined;
  activeStatusLabel?: string | undefined;
  activeTurnInProgress?: boolean;
  activeTurnId?: TurnId | null;
  activeTurnStartedAt: string | null;
  turnDiffSummaryByAssistantMessageId: ReadonlyMap<MessageId, TurnDiffSummary>;
  revertTurnCountByUserMessageId: ReadonlyMap<MessageId, number>;
  /** Side answers in a room (sideAnswers.ts), placed among the rows. */
  sideAnswers?: ReadonlyArray<SideAnswerView> | undefined;
  /** Side answers whose one-line status was opened to watch the answer. */
  expandedSideTurnIds?: ReadonlySet<SideTurnId> | undefined;
  /** The room's open requests, by the message that made them: an agent's
   *  ask its call stopped waiting for (`replyAsMessage`) is no longer what
   *  the call is on. */
  openAgentRequestByMessageId?: ReadonlyMap<MessageId, OrchestrationAgentRequest> | undefined;
  /** The agent holding the thread: it owns work no message names yet. Null
   *  (the default) for the thread's own. */
  workingParticipantId?: ThreadParticipantId | null | undefined;
  /** What the room calls each agent, for the name the working row gives the
   *  one a room tool call is aimed at. */
  roomAgentNames?: RoomAgentNames | null | undefined;
}): MessagesTimelineRow[] {
  const nextRows: MessagesTimelineRow[] = [];
  const shownPageIds = new Set<string>();
  const visibleTimelineEntries = settleTurnsAboveResponse(
    deriveVisibleTimelineEntries(input),
    input.isWorking ? (input.activeTurnId ?? null) : null,
  );
  const sidePlans =
    (input.sideAnswers?.length ?? 0) === 0
      ? []
      : planSideExchanges({
          views: input.sideAnswers ?? [],
          expanded: input.expandedSideTurnIds ?? NO_EXPANDED_SIDE_ANSWERS,
        });
  const sideBreaks = sideExchangeBreaks(sidePlans);
  // Turn-request markers are hidden once a turn settles, so read them from
  // the full entry list rather than the visible one.
  const turnRequestedAts = input.timelineEntries
    .flatMap((entry) =>
      entry.kind === "work" && entry.entry.providerLifecyclePhase === "preparing"
        ? [entry.createdAt]
        : [],
    )
    .toSorted();
  const terminalAssistantMessageIds = deriveTerminalAssistantMessageIds(visibleTimelineEntries);
  const modelFallbackByTurn = deriveModelFallbackByTurn(visibleTimelineEntries);
  const supersededRunningCommandEntryIds =
    inferSupersededRunningCommandEntryIds(visibleTimelineEntries);
  /** Turns whose tracker has already been handed to an earlier group. */
  const trackedTurnIds = new Set<TurnId>();
  // Everything after the last user message is the active exchange. Reasoning
  // and lifecycle entries arrive without turn ids, so position is the reliable
  // signal across providers.
  const lastUserMessageIndex = visibleTimelineEntries.findLastIndex(
    (entry) => entry.kind === "message" && startsExchange(entry.message),
  );

  for (let index = 0; index < visibleTimelineEntries.length; index += 1) {
    const timelineEntry = visibleTimelineEntries[index];
    if (!timelineEntry) {
      continue;
    }

    if (timelineEntry.kind === "work") {
      const groupedEntries: WorkLogEntry[] = [];
      const agentAnchorEntries: WorkLogEntry[] = [];
      const collect = (entry: Extract<TimelineEntry, { kind: "work" }>) => {
        const settled = settleSupersededRunningCommandEntry(
          entry.entry,
          supersededRunningCommandEntryIds,
        );
        // Private thinking and provider lifecycle narration are status, not
        // history: the working anchor's label names them while they are
        // current, so a row that would vanish moments later never mounts at
        // all. Private thinking becomes a real row only if it settles into a
        // readable summary (the log drops the redacted flag) or fails.
        if (settled.redactedThinking && settled.executionState !== "failed") {
          return;
        }
        if (settled.providerLifecyclePhase) {
          return;
        }
        (isAgentLifecycleEntry(entry) ? agentAnchorEntries : groupedEntries).push(settled);
      };
      collect(timelineEntry);
      let cursor = index + 1;
      while (cursor < visibleTimelineEntries.length) {
        const nextEntry = visibleTimelineEntries[cursor];
        if (!nextEntry || nextEntry.kind !== "work") break;
        // A side exchange goes between these two steps.
        if (
          sideBreaks.length > 0 &&
          breaksWithin(
            sideBreaks,
            Date.parse(visibleTimelineEntries[cursor - 1]!.createdAt),
            Date.parse(nextEntry.createdAt),
          )
        ) {
          break;
        }
        collect(nextEntry);
        cursor += 1;
      }
      // First group of a turn wins its tracker: that is where the turn's story
      // starts, and while the turn runs it is where the live status line belongs.
      const trackerTurnIds: TurnId[] = [];
      for (const entry of [...groupedEntries, ...agentAnchorEntries]) {
        const turnId = entry.turnId;
        if (turnId === null || turnId === undefined || trackedTurnIds.has(turnId)) {
          continue;
        }
        trackedTurnIds.add(turnId);
        trackerTurnIds.push(turnId);
      }
      const trackerAgentSpawnIds =
        trackerTurnIds.length === 0
          ? deriveTrackerAgentSpawnIds(groupedEntries, agentAnchorEntries)
          : [];
      nextRows.push({
        ...UNPLACED,
        kind: "work",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        groupedEntries,
        agentAnchorEntries,
        trackerTurnIds,
        trackerAgentSpawnIds,
        isLive: false,
        liveStartedAt: null,
        inActiveExchange: input.isWorking && index > lastUserMessageIndex,
        folded: false,
      });
      index = cursor - 1;
      continue;
    }

    if (timelineEntry.kind === "proposed-plan") {
      nextRows.push({
        ...UNPLACED,
        kind: "proposed-plan",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        proposedPlan: timelineEntry.proposedPlan,
      });
      continue;
    }

    if (timelineEntry.kind === "subagent-result") {
      nextRows.push({
        ...UNPLACED,
        kind: "subagent-result",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        result: timelineEntry.result,
      });
      continue;
    }

    if (timelineEntry.kind === "workflow-run") {
      nextRows.push({
        ...UNPLACED,
        kind: "workflow-run",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        run: timelineEntry.run,
      });
      continue;
    }

    if (timelineEntry.kind === "mcp-app") {
      nextRows.push({
        ...UNPLACED,
        kind: "mcp-app",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        app: timelineEntry.app,
      });
      continue;
    }

    // A running agent's streamed commentary never reaches the conversation:
    // the turn's activity row summarizes what is running, and the rail carries
    // the detail. Filtered with the agent-attributed entries above; this arm
    // only narrows the type for the message handling below.
    if (timelineEntry.kind === "subagent-live") {
      continue;
    }

    if (timelineEntry.kind === "fork-context") {
      nextRows.push({
        ...UNPLACED,
        kind: "fork-context",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        forkContext: timelineEntry.forkContext,
      });
      continue;
    }

    if (timelineEntry.kind === "moved-in") {
      nextRows.push({
        ...UNPLACED,
        kind: "moved-in",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        movedIn: timelineEntry.movedIn,
      });
      continue;
    }

    if (timelineEntry.kind === "command-request") {
      nextRows.push({
        ...UNPLACED,
        kind: "command-request",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        request: timelineEntry.request,
      });
      continue;
    }

    if (timelineEntry.kind === "page") {
      nextRows.push({
        ...UNPLACED,
        kind: "page",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        page: timelineEntry.page,
        updated: shownPageIds.has(timelineEntry.page.pageId),
      });
      shownPageIds.add(timelineEntry.page.pageId);
      continue;
    }

    const assistantTurnStillInProgress =
      timelineEntry.message.role === "assistant" &&
      input.activeTurnInProgress === true &&
      input.activeTurnId != null &&
      timelineEntry.message.turnId === input.activeTurnId;
    const assistantTurnInProgress =
      timelineEntry.message.role === "assistant" &&
      (timelineEntry.message.streaming || assistantTurnStillInProgress);

    const assistantTurnDiffSummary =
      timelineEntry.message.role === "assistant"
        ? input.turnDiffSummaryByAssistantMessageId.get(timelineEntry.message.id)
        : undefined;

    nextRows.push({
      ...UNPLACED,
      kind: "message",
      id: timelineEntry.id,
      createdAt: timelineEntry.createdAt,
      message: timelineEntry.message,
      settledNote: false,
      turnSummary: null,
      showAssistantCopyButton:
        timelineEntry.message.role === "assistant" &&
        terminalAssistantMessageIds.has(timelineEntry.message.id),
      assistantCopyStreaming: assistantTurnInProgress,
      assistantTurnInProgress,
      assistantModelFallback:
        timelineEntry.message.role === "assistant"
          ? modelFallbackByTurn.get(turnSignalKey(timelineEntry.message.turnId))
          : undefined,
      assistantTurnDiffSummary:
        assistantTurnDiffSummary?.turnId === timelineEntry.message.turnId
          ? assistantTurnDiffSummary
          : undefined,
      revertTurnCount:
        timelineEntry.message.role === "user"
          ? input.revertTurnCountByUserMessageId.get(timelineEntry.message.id)
          : undefined,
    });
  }

  const liveAgentCount = input.liveAgentCount ?? 0;
  if (input.isWorking) {
    markLatestLiveWorkRow(nextRows, input.activeTurnId ?? null, input.activeTurnStartedAt);
  }
  const settledRows = settleFinishedTurns(nextRows, {
    isWorking: input.isWorking,
    activeTurnId: input.activeTurnId ?? null,
    turnRequestedAts,
  });
  const { rows, tracker } =
    input.isWorking || liveAgentCount > 0
      ? takeLiveAgentTrackers(settledRows, input.liveAgentTrackerKeys ?? NO_LIVE_AGENT_TRACKER_KEYS)
      : { rows: settledRows, tracker: NO_TAKEN_TRACKER };
  // The side answer the working row narrates, if any: its own line leaves the
  // step to the working row.
  let narratedSideTurnId: SideTurnId | null = null;
  if (input.isWorking) {
    // The working row is the turn's one fixed anchor: it renders for the whole
    // turn, whatever the tail is, so the live node, the timer, and the agent
    // tracker never teleport between homes. Its word is the step running now.
    const { narrates, ...anchor } = resolveLiveAnchor(nextRows, visibleTimelineEntries, input);
    narratedSideTurnId = narrates;
    rows.push({
      ...UNPLACED,
      kind: "working",
      id: "working-indicator-row",
      createdAt: input.activeTurnStartedAt,
      ...anchor,
      command: resolveLiveCommand(visibleTimelineEntries),
      ...tracker,
    });
  } else if (liveAgentCount > 0) {
    // The turn settled but agents it delegated to are still going: the anchor
    // stays as their tracker, without a turn timer, until the last one lands.
    // The tracker beside the word already counts the agents, so when the turn
    // also waits on something else (a test run), the word names that instead.
    const waitingBesidesAgents =
      input.isWaitingOnBackgroundTasks && input.awaitedTasks
        ? formatAwaitedTasks(input.awaitedTasks, ["workflow", "command", "other"])
        : null;
    rows.push({
      ...UNPLACED,
      kind: "working",
      id: "working-indicator-row",
      createdAt: null,
      label: waitingBesidesAgents
        ? `Waiting on ${waitingBesidesAgents}`
        : liveAgentCount === 1
          ? "Agent working"
          : "Agents working",
      thought: null,
      command: null,
      ...tracker,
    });
  } else if (input.isWaitingOnBackgroundTasks) {
    // The turn settled but a background task (a command, an agent) will wake
    // it: the anchor stays up as a wait, without a turn timer, and says what
    // it waits on when the server described it.
    const waitingOn = input.awaitedTasks ? formatAwaitedTasks(input.awaitedTasks) : null;
    // A lone workflow says where it is instead (its card has the rest).
    const soleWorkflow = input.awaitedTasks ? formatSoleAwaitedWorkflow(input.awaitedTasks) : null;
    rows.push({
      ...UNPLACED,
      kind: "working",
      id: "working-indicator-row",
      createdAt: null,
      label: soleWorkflow ?? (waitingOn ? `Waiting on ${waitingOn}` : "Waiting"),
      thought: null,
      command: null,
      ...tracker,
    });
  }

  // A settled thread an agent will come back to says when, at the tail.
  if (!input.isWorking && input.checkBacks && input.checkBacks.length > 0) {
    rows.push({
      ...UNPLACED,
      kind: "check-back",
      id: "check-back-row",
      createdAt: null,
      checkBacks: input.checkBacks,
    });
  }

  return placeRows(
    placeSideExchanges(foldFinishedStretches(rows, input.isWorking), sidePlans, {
      roomAgentNames: input.roomAgentNames ?? null,
      narratedSideTurnId,
    }),
    input.isWorking,
    input.workingParticipantId ?? null,
  );
}

interface TakenTracker {
  readonly trackerTurnIds: TurnId[];
  readonly trackerAgentSpawnIds: string[];
}

const NO_TAKEN_TRACKER: TakenTracker = { trackerTurnIds: [], trackerAgentSpawnIds: [] };

/**
 * The working row takes the tracker of every turn whose agents still work,
 * from the footer under the turn's answer or from the group that carries it.
 * While any of a turn's agents run, its count sits on the "Agents working"
 * row with the agents' live lines, not twice; when the last one lands, the
 * working row leaves and the count is back on its turn.
 */
function takeLiveAgentTrackers(
  rows: MessagesTimelineRow[],
  keys: LiveAgentTrackerKeys,
): { rows: MessagesTimelineRow[]; tracker: TakenTracker } {
  if (keys.turnIds.size === 0 && keys.spawnCallIds.size === 0) {
    return { rows, tracker: NO_TAKEN_TRACKER };
  }
  const turnIds = new Set<TurnId>();
  const spawnIds = new Set<string>();
  const take = (tracker: TurnSummary | Extract<MessagesTimelineRow, { kind: "work" }>) => {
    const tracksLiveAgent =
      tracker.trackerTurnIds.some((turnId) => keys.turnIds.has(turnId)) ||
      tracker.trackerAgentSpawnIds.some((spawnId) => keys.spawnCallIds.has(spawnId));
    if (tracksLiveAgent) {
      tracker.trackerTurnIds.forEach((turnId) => turnIds.add(turnId));
      tracker.trackerAgentSpawnIds.forEach((spawnId) => spawnIds.add(spawnId));
    }
    return tracksLiveAgent;
  };
  const taken = rows.map((row): MessagesTimelineRow => {
    if (row.kind === "work" && take(row)) {
      return { ...row, trackerTurnIds: [], trackerAgentSpawnIds: [] };
    }
    if (row.kind === "message" && row.turnSummary !== null && take(row.turnSummary)) {
      return {
        ...row,
        turnSummary: { ...row.turnSummary, trackerTurnIds: [], trackerAgentSpawnIds: [] },
      };
    }
    return row;
  });
  return turnIds.size === 0 && spawnIds.size === 0
    ? { rows, tracker: NO_TAKEN_TRACKER }
    : {
        rows: taken,
        tracker: { trackerTurnIds: [...turnIds], trackerAgentSpawnIds: [...spawnIds] },
      };
}

const NO_EXPANDED_SIDE_ANSWERS: ReadonlySet<SideTurnId> = new Set();

/** How long a stretch of work took: its first step's start to its last
 *  finished step's end. Null while nothing in it has finished. */
export function stretchDurationMs(entries: ReadonlyArray<WorkLogEntry>): number | null {
  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;
  for (const entry of entries) {
    const started = Date.parse(entry.createdAt);
    if (Number.isFinite(started)) start = Math.min(start, started);
    const finished = entry.completedAt ? Date.parse(entry.completedAt) : Number.NaN;
    if (entry.executionState !== "running" && Number.isFinite(finished)) {
      end = Math.max(end, finished);
    }
  }
  return end > start ? end - start : null;
}

/**
 * Folds each stretch of work its agent has moved on from: it wrote again after
 * the stretch (a note, or in a room the hand-off that ends its turn), newer
 * steps landed past whatever split them from the stretch (a finished agent's
 * receipt, a side question, another agent's reply), or the stretch's exchange
 * is over. The one stretch the working agent is still on stays open. The last
 * stretch of a turn folds when the answer starts, so the turn ending folds
 * nothing above the answer.
 */
function foldFinishedStretches(
  rows: ReadonlyArray<MessagesTimelineRow>,
  isWorking: boolean,
): MessagesTimelineRow[] {
  const folded = [...rows];
  let agentMovedOn = false;
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const row = rows[index]!;
    // Walking back from the end: the agent's own words (a note, an answer, a
    // hand-off) and newer steps fold what came before them in that exchange;
    // your message starts an earlier exchange. Any other message between
    // agents (an invite, a reply that arrives while it works) leaves the
    // working agent on its stretch.
    if (row.kind === "message") {
      if (startsExchange(row.message)) agentMovedOn = false;
      else if (row.message.role === "assistant" || row.message.requestKind === "hand_off") {
        agentMovedOn = true;
      }
      continue;
    }
    if (row.kind !== "work") {
      continue;
    }
    const fold = agentMovedOn || !(isWorking && row.inActiveExchange);
    if (row.folded !== fold) {
      folded[index] = { ...row, folded: fold };
    }
    if (row.groupedEntries.some((entry) => !isSilentWorkLogEntry(entry))) {
      agentMovedOn = true;
    }
  }
  return folded;
}

type MessageRow = Extract<MessagesTimelineRow, { kind: "message" }>;

/** Rows the agent produced, as opposed to your messages and the page between
 *  turns. */
function isAgentRow(row: MessagesTimelineRow): boolean {
  return (
    row.kind === "work" ||
    row.kind === "subagent-result" ||
    row.kind === "workflow-run" ||
    row.kind === "mcp-app" ||
    row.kind === "working" ||
    (row.kind === "message" && row.message.role === "assistant")
  );
}

/** A page or a command card: part of the agent's answer, on a quiet panel of
 *  its own between the steps and the reply. */
function isAnswerPanel(row: MessagesTimelineRow): boolean {
  return row.kind === "page" || row.kind === "command-request";
}

/** A step group with nothing to draw (only running steps, which the working
 *  row names, or agent plumbing whose tracker moved to the footer) takes no
 *  room, so it cannot end an agent's line or set the room above the row after
 *  it. */
function drawsNothing(row: MessagesTimelineRow, isWorking: boolean): boolean {
  if (row.kind !== "work") {
    return false;
  }
  const showsTracker =
    (row.trackerTurnIds.length > 0 || row.trackerAgentSpawnIds.length > 0) &&
    !(isWorking && row.inActiveExchange);
  return (
    !showsTracker &&
    row.groupedEntries.every(
      (entry) => entry.executionState === "running" || isSilentWorkLogEntry(entry),
    )
  );
}

/**
 * Places each row: on its agent's line (placeAgentLines), and with or without
 * room above it. Messages always keep that room, so whichever message turns
 * out to be the answer has space above it, and so do pages and command cards,
 * whose panels keep half a gap on each side. Other rows keep it where the
 * agent's work meets your side of the chat: the first step after your message,
 * or your next message after the agent's work or a panel. Rows that draw
 * nothing are skipped, so right after you send, the working row keeps its room
 * even with the turn request's empty group above it.
 */
function placeRows(
  rows: ReadonlyArray<MessagesTimelineRow>,
  isWorking: boolean,
  workingParticipantId: ThreadParticipantId | null,
): MessagesTimelineRow[] {
  const visible = rows.map((row) => !drawsNothing(row, isWorking));
  const agentLines = placeAgentLines(rows, visible, workingParticipantId);
  // The nearest row above that draws something. A side exchange is placed by
  // sideAnswers.ts and, around the working agent's rows, reads like your side.
  const agentsOwn = (row: MessagesTimelineRow) => isAgentRow(row) && !isSideRow(row);
  let above: MessagesTimelineRow | undefined;
  return rows.map((row, index) => {
    const agentLine = agentLines[index];
    if (isSideRow(row)) {
      if (visible[index]) {
        above = row;
      }
      return withAgentLine(row, agentLine);
    }
    const padTop =
      (row.kind === "message" && row.message.role === "assistant") || isAnswerPanel(row)
        ? true
        : agentsOwn(row)
          ? above === undefined ||
            !agentsOwn(above) ||
            (above.kind === "message" && above.turnSummary !== null)
          : above !== undefined && (agentsOwn(above) || isAnswerPanel(above));
    if (visible[index]) {
      above = row;
    }
    return withAgentLine(row.padTop === padTop ? row : { ...row, padTop }, agentLine);
  });
}

function withAgentLine(row: MessagesTimelineRow, agentLine: AgentLine | undefined) {
  return sameAgentLine(row.agentLine, agentLine) ? row : { ...row, agentLine };
}

function sameAgentLine(a: AgentLine | undefined, b: AgentLine | undefined): boolean {
  return (
    a === b ||
    (a !== undefined &&
      b !== undefined &&
      a.placement === b.placement &&
      a.agent === b.agent &&
      a.turnId === b.turnId &&
      a.heads === b.heads &&
      a.nested === b.nested &&
      a.echoesAnswer === b.echoesAnswer &&
      a.asWritten === b.asWritten)
  );
}

/**
 * Each agent's lines. A stretch is one agent's own rows (notes,
 * steps, the working line, its answer) from where it starts, after your
 * message or another agent's, to its answer or working line. Side exchanges
 * between them stay on the line, the other agent's answer set in under the
 * question; an exchange the agent asked just before its turn stopped stays on
 * its line too. A side answer outside any stretch (posted after the stretch
 * ended) is a stretch of its answerer's. A message one agent wrote to another
 * (a hand-off, a reply) is a stretch of its own for the agent that wrote it.
 */
function placeAgentLines(
  rows: ReadonlyArray<MessagesTimelineRow>,
  visible: ReadonlyArray<boolean>,
  workingParticipantId: ThreadParticipantId | null,
): Array<AgentLine | undefined> {
  const owners = roomTurnOwners(
    rows.flatMap((row) => (row.kind === "message" ? [row.message] : [])),
  );
  // The turn a row of the agent's own names, if any.
  const turnOf = (row: MessagesTimelineRow): TurnId | null =>
    row.kind === "message"
      ? (row.message.turnId ?? null)
      : row.kind === "work"
        ? (row.groupedEntries.find((entry) => entry.turnId != null)?.turnId ?? null)
        : row.kind === "subagent-result"
          ? (row.result.turnId ?? null)
          : row.kind === "workflow-run"
            ? row.run.turnId
            : row.kind === "mcp-app"
              ? row.app.turnId
              : row.kind === "page"
                ? row.page.turnId
                : row.kind === "command-request"
                  ? row.request.turnId
                  : null;
  // The agent whose own row this is; undefined for rows that are not one
  // agent's own work (your messages, side exchanges, plans). A page or a
  // command card is the agent's that showed it. A turn no message names yet is
  // the working agent's; null names the thread's own.
  const agentOf = (row: MessagesTimelineRow): ThreadParticipantId | null | undefined => {
    if (row.kind === "page") {
      return row.page.participantId;
    }
    if (row.kind === "command-request") {
      return row.request.participantId;
    }
    if (isSideRow(row) || !isAgentRow(row)) {
      return undefined;
    }
    if (row.kind === "message") {
      return row.message.participantId ?? null;
    }
    const turnId = turnOf(row);
    return turnId !== null && owners.has(turnId)
      ? (owners.get(turnId) ?? null)
      : workingParticipantId;
  };
  const breaksStretch = (row: MessagesTimelineRow) =>
    row.kind === "fork-context" ||
    row.kind === "moved-in" ||
    (row.kind === "message" &&
      !isSideRow(row) &&
      (startsExchange(row.message) || row.message.fromAgent !== undefined));
  const isAnswer = (row: MessagesTimelineRow) => row.kind === "message" && row.turnSummary !== null;

  // Side exchanges: who answers each (null: the thread's own agent), how it
  // was when it answered, and who asked (undefined: the user).
  const answerer = new Map<SideTurnId, ThreadParticipantId | null>();
  const answeredAs = new Map<SideTurnId, MessageAgentModel>();
  const asker = new Map<SideTurnId, ThreadParticipantId | null | undefined>();
  for (const row of rows) {
    if (row.kind === "message" && row.message.sideTurnId !== undefined) {
      answerer.set(row.message.sideTurnId, row.message.participantId ?? null);
      const stamp = authorStamp(row.message);
      if (stamp !== null) {
        answeredAs.set(row.message.sideTurnId, stamp);
      }
      if (row.message.role === "user") {
        asker.set(row.message.sideTurnId, row.message.fromAgent?.participantId);
      }
    } else if (row.kind === "side-status") {
      answerer.set(row.sideTurnId, row.participantId);
      asker.set(row.sideTurnId, row.question.fromAgent?.participantId);
    }
  }
  // Who answers `sideTurnId`, the thread's own agent included; `fallback`
  // when no row says.
  const answererOf = (
    sideTurnId: SideTurnId,
    fallback: ThreadParticipantId | null,
  ): ThreadParticipantId | null => {
    const known = answerer.get(sideTurnId);
    return known === undefined ? fallback : known;
  };
  // An answerer's rows in an exchange: its steps and its answer. The first of
  // them carries its name.
  const answeringTurn = (row: MessagesTimelineRow): SideTurnId | undefined =>
    row.kind === "work"
      ? row.sideTurnId
      : row.kind === "message" && row.message.role === "assistant"
        ? row.message.sideTurnId
        : undefined;
  const headsAnswer = new Set<number>();
  const namedTurns = new Set<SideTurnId>();
  for (const [index, row] of rows.entries()) {
    const sideTurnId = answeringTurn(row);
    if (sideTurnId !== undefined && visible[index] && !namedTurns.has(sideTurnId)) {
      namedTurns.add(sideTurnId);
      headsAnswer.add(index);
    }
  }
  const sideTurnOf = (row: MessagesTimelineRow): SideTurnId | undefined =>
    row.kind === "message" || row.kind === "work"
      ? (answeringTurn(row) ?? (row.kind === "message" ? row.message.sideTurnId : undefined))
      : row.kind === "side-status"
        ? row.sideTurnId
        : undefined;

  const lines: Array<AgentLine | undefined> = rows.map(() => undefined);
  let previousVisible: MessagesTimelineRow | undefined;
  for (const [index, row] of rows.entries()) {
    if (row.kind === "message" && row.message.fromAgent !== undefined && !isSideRow(row)) {
      lines[index] = {
        placement: "single",
        agent: row.message.fromAgent.participantId,
        turnId: row.message.turnId ?? null,
        heads: true,
        nested: null,
        echoesAnswer:
          row.message.requestKind === "reply" &&
          previousVisible?.kind === "message" &&
          previousVisible.message.role === "assistant" &&
          previousVisible.message.text.trim() === row.message.text.trim(),
        asWritten: askerStamp(row.message),
      };
    }
    if (visible[index]) {
      previousVisible = row;
    }
  }

  // Another agent answering inside a stretch hangs on a line of its own, one
  // lane in: from its name down to the line that says it is answering, or to
  // its answer once it is done. Rows that draw nothing stay off its ends.
  const placeNested = (first: number, last: number, agent: ThreadParticipantId | null) => {
    const placements = new Map<number, AgentLinePlacement>();
    for (let index = first; index <= last; index += 1) {
      const sideTurnId = isNestedSideRow(rows[index]!, agent)
        ? sideTurnOf(rows[index]!)
        : undefined;
      if (sideTurnId === undefined) {
        continue;
      }
      let end = index;
      while (
        end < last &&
        isNestedSideRow(rows[end + 1]!, agent) &&
        sideTurnOf(rows[end + 1]!) === sideTurnId
      ) {
        end += 1;
      }
      const shown: number[] = [];
      for (let cursor = index; cursor <= end; cursor += 1) {
        if (visible[cursor]) shown.push(cursor);
      }
      const top = shown[0] ?? end + 1;
      const bottom = shown.at(-1) ?? index - 1;
      for (let cursor = index; cursor <= end; cursor += 1) {
        placements.set(
          cursor,
          top === bottom || cursor < top || cursor > bottom
            ? "single"
            : cursor === top
              ? "start"
              : cursor === bottom
                ? "end"
                : "through",
        );
      }
      index = end;
    }
    return placements;
  };

  // One agent's stretch from `first` to `last`. The line bends into the last
  // row when it is the agent's own, and stops under a side exchange it ends on.
  // An answer opens at the first of its turn's pages and command cards right
  // above it (settled there by settleTurnsAboveResponse), as does a turn that
  // ended on them with no reply: the line bends into that panel, or, when the
  // stretch starts there (its steps are gone), the panel carries the name.
  const placeStretch = (first: number, last: number, agent: ThreadParticipantId | null) => {
    const nested = placeNested(first, last, agent);
    const closing = rows[last]!;
    const answerTurn = isAnswer(closing) || isAnswerPanel(closing) ? turnOf(closing) : null;
    let bend = last;
    // A panel always names its turn, so with no answer the walk stops at once.
    for (let index = last - 1; index >= first; index -= 1) {
      const row = rows[index]!;
      if (!isAnswerPanel(row) || turnOf(row) !== answerTurn) {
        break;
      }
      bend = index;
    }
    let turnId: TurnId | null = null;
    for (let index = first; index <= last && turnId === null; index += 1) {
      turnId = isSideRow(rows[index]!) ? null : turnOf(rows[index]!);
    }
    // The first stamp holds even if a late write of an older turn lands in it.
    let stretchStamp: MessageAgentModel | null = null;
    for (let index = first; index <= last && stretchStamp === null; index += 1) {
      const row = rows[index]!;
      if (
        row.kind === "message" &&
        !isSideRow(row) &&
        row.message.role === "assistant" &&
        (row.message.participantId ?? null) === agent
      ) {
        stretchStamp = authorStamp(row.message);
      }
    }
    for (let index = first; index <= last; index += 1) {
      const row = rows[index]!;
      const sideTurnId = isSideRow(row) ? sideTurnOf(row) : undefined;
      const answering = answeringTurn(row) !== undefined;
      lines[index] = {
        placement:
          first === last
            ? "single"
            : index > bend
              ? "past"
              : index === first
                ? bend === first
                  ? "single"
                  : "start"
                : index === bend && !isSideRow(row)
                  ? "end"
                  : "through",
        agent:
          sideTurnId === undefined
            ? agent
            : answering
              ? answererOf(sideTurnId, agent)
              : row.kind === "message" && row.message.fromAgent !== undefined
                ? row.message.fromAgent.participantId
                : agent,
        turnId: sideTurnId === undefined ? turnId : null,
        heads: sideTurnId === undefined ? index === first : headsAnswer.has(index),
        nested: nested.get(index) ?? null,
        echoesAnswer: false,
        asWritten:
          sideTurnId === undefined
            ? stretchStamp
            : answering
              ? (answeredAs.get(sideTurnId) ?? null)
              : row.kind === "message" && row.message.fromAgent !== undefined
                ? askerStamp(row.message)
                : stretchStamp,
      };
    }
  };

  let start = 0;
  while (start < rows.length) {
    const agent = agentOf(rows[start]!);
    if (agent === undefined || !visible[start]) {
      start += 1;
      continue;
    }
    let last = start;
    if (!isAnswer(rows[start]!)) {
      for (let cursor = start + 1; cursor < rows.length; cursor += 1) {
        const row = rows[cursor]!;
        if (breaksStretch(row)) {
          break;
        }
        const rowAgent = agentOf(row);
        if (rowAgent === undefined) {
          continue;
        }
        if (rowAgent !== agent) {
          break;
        }
        if (visible[cursor]) {
          last = cursor;
        }
        if (isAnswer(row)) {
          break;
        }
      }
      // A turn that stopped right after asking another agent keeps that
      // exchange on its line.
      for (let cursor = last + 1; !isAnswer(rows[last]!) && cursor < rows.length; cursor += 1) {
        const row = rows[cursor]!;
        const sideTurnId = isSideRow(row) ? sideTurnOf(row) : undefined;
        if (sideTurnId === undefined) {
          if (visible[cursor]) break;
          continue;
        }
        if (asker.get(sideTurnId) !== agent) {
          break;
        }
        if (visible[cursor]) {
          last = cursor;
        }
      }
    }
    placeStretch(start, last, agent);
    start = last + 1;
  }

  // A side answer no stretch holds is a stretch of its answerer's: its steps
  // and its answer, named at the first of them.
  for (let first = 0; first < rows.length; first += 1) {
    const sideTurnId = answeringTurn(rows[first]!);
    if (sideTurnId === undefined || lines[first] !== undefined || !visible[first]) {
      continue;
    }
    let last = first;
    for (let cursor = first + 1; cursor < rows.length; cursor += 1) {
      const row = rows[cursor]!;
      if (answeringTurn(row) === sideTurnId && lines[cursor] === undefined) {
        if (visible[cursor]) last = cursor;
        continue;
      }
      if (visible[cursor]) break;
    }
    const agent = answererOf(sideTurnId, null);
    for (let index = first; index <= last; index += 1) {
      lines[index] = {
        placement:
          first === last
            ? "single"
            : index === first
              ? "start"
              : index === last && rows[index]!.kind === "message"
                ? "end"
                : "through",
        agent,
        turnId: null,
        heads: index === first,
        nested: null,
        echoesAnswer: false,
        asWritten: answeredAs.get(sideTurnId) ?? null,
      };
    }
    first = last;
  }
  return lines;
}

/** How an assistant message's author was when it wrote the message. */
function authorStamp(message: ChatMessage): MessageAgentModel | null {
  return message.agentModels?.[roomAgentKey(message.participantId)] ?? null;
}

/** How the agent that wrote a request was when it wrote it. */
function askerStamp(message: ChatMessage): MessageAgentModel | null {
  return message.fromAgent === undefined
    ? null
    : (message.agentModels?.[roomAgentKey(message.fromAgent.participantId)] ?? null);
}

/** Another agent's part of a side exchange: its answer, its steps, and how
 *  an agent's request ended. The question, and the line under the user's own
 *  question, stay out. */
function isNestedSideRow(row: MessagesTimelineRow, agent: ThreadParticipantId | null): boolean {
  switch (row.kind) {
    case "message":
      return (
        row.message.sideTurnId !== undefined &&
        row.message.role === "assistant" &&
        (row.message.participantId ?? null) !== agent
      );
    case "work":
      return row.sideTurnId !== undefined;
    case "side-status":
      return row.question.fromAgent !== undefined;
    default:
      return false;
  }
}

/**
 * A finished turn keeps its story where it is: nothing folds, so nothing above
 * the answer moves when the turn ends. The turn's last message becomes its
 * answer and carries the turn's footer, which takes the working row's place at
 * the tail, and the notes before it fade. Settling goes turn by turn, so a turn
 * resumed after a background task leaves the earlier turn's footer in place.
 * A turn that ended without a message has no footer, and its first group keeps
 * the agent tracker.
 */
function settleFinishedTurns(
  rows: ReadonlyArray<MessagesTimelineRow>,
  options: {
    readonly isWorking: boolean;
    readonly activeTurnId: TurnId | null;
    readonly turnRequestedAts: ReadonlyArray<string>;
  },
): MessagesTimelineRow[] {
  const result = [...rows];
  const requestTimes = options.turnRequestedAts
    .map((requestedAt) => Date.parse(requestedAt))
    .filter(Number.isFinite);
  const lastUserIndex = result.findLastIndex(
    (row) => row.kind === "message" && startsExchange(row.message),
  );
  let spanStart = 0;
  let userMessageAt: string | null = null;
  let previousAnswerEndMs = Number.NEGATIVE_INFINITY;
  for (let index = 0; index < result.length; index += 1) {
    const row = result[index]!;
    if (row.kind !== "message") {
      continue;
    }
    if (startsExchange(row.message)) {
      spanStart = index + 1;
      userMessageAt = row.message.createdAt;
      previousAnswerEndMs = Number.NEGATIVE_INFINITY;
      continue;
    }
    // The same signal that keeps the working row up, so the row and the footer
    // swap in one render. A reply the provider did not tie to a turn stays
    // live until the exchange it belongs to ends.
    const running =
      row.message.streaming ||
      (options.isWorking &&
        index > lastUserIndex &&
        (!row.message.turnId || row.message.turnId === options.activeTurnId));
    if (row.message.role !== "assistant" || !row.showAssistantCopyButton || running) {
      continue;
    }

    const span = result.slice(spanStart, index);
    const work = summarizeTurnWork(span);
    span.forEach((spanRow, offset) => {
      if (spanRow.kind === "message" && spanRow.message.role === "assistant") {
        result[spanStart + offset] = { ...spanRow, settledNote: true };
      } else if (
        spanRow.kind === "work" &&
        (spanRow.trackerTurnIds.length > 0 || spanRow.trackerAgentSpawnIds.length > 0)
      ) {
        result[spanStart + offset] = { ...spanRow, trackerTurnIds: [], trackerAgentSpawnIds: [] };
      }
    });
    const startedAtMs =
      userMessageAt !== null
        ? Date.parse(userMessageAt)
        : resumedTurnStartMs(span, row, requestTimes, previousAnswerEndMs);
    result[index] = {
      ...row,
      turnSummary: {
        workedMs: workedDurationMs(startedAtMs, span, row, requestTimes),
        // The checkpoint diff lands a beat after the turn; until then the
        // turn's own edits are the count.
        editedFileCount: row.assistantTurnDiffSummary?.files.length ?? work.editedFileCount,
        checks: work.checks,
        trackerTurnIds: work.trackerTurnIds,
        trackerAgentSpawnIds: work.trackerAgentSpawnIds,
      },
    };
    spanStart = index + 1;
    userMessageAt = null;
    previousAnswerEndMs = Date.parse(row.message.completedAt ?? row.message.createdAt);
  }
  return result;
}

/**
 * When a turn with no message of its own started (a Retry, a turn resumed
 * after a background task): at its first request or first activity, whichever
 * came first, so the wait before it is not work.
 */
function resumedTurnStartMs(
  span: ReadonlyArray<MessagesTimelineRow>,
  answer: MessageRow,
  requestTimes: ReadonlyArray<number>,
  previousAnswerEndMs: number,
): number {
  let startedAt = Date.parse(answer.message.createdAt);
  const firstRequest = requestTimes[firstIndexAfter(requestTimes, previousAnswerEndMs)];
  if (firstRequest !== undefined && firstRequest < startedAt) {
    startedAt = firstRequest;
  }
  // Rows run in time order, so the turn's first activity opens its first row.
  const firstRowAt = span[0]?.createdAt ? Date.parse(span[0].createdAt) : Number.NaN;
  return firstRowAt < startedAt ? firstRowAt : startedAt;
}

/**
 * How long the agent worked from the turn's start to its answer. A later turn
 * request in between (a Retry, a turn resumed after a background task) starts
 * the clock again: the wait before it, back to the last thing that happened,
 * is not work.
 */
function workedDurationMs(
  startedAt: number,
  rows: ReadonlyArray<MessagesTimelineRow>,
  answer: MessageRow,
  requestTimes: ReadonlyArray<number>,
): number | null {
  const endedAt = Date.parse(answer.message.completedAt ?? answer.message.createdAt);
  if (!Number.isFinite(startedAt) || !Number.isFinite(endedAt) || endedAt < startedAt) {
    return null;
  }
  let idleMs = 0;
  let clockStartedAt = startedAt;
  for (
    let requestIndex = firstIndexAfter(requestTimes, startedAt);
    requestIndex < requestTimes.length && requestTimes[requestIndex]! <= endedAt;
    requestIndex += 1
  ) {
    const requestedAt = requestTimes[requestIndex]!;
    idleMs += requestedAt - lastActivityAtOrBefore(rows, requestedAt, clockStartedAt);
    clockStartedAt = requestedAt;
  }
  return endedAt - startedAt - idleMs;
}

/** The latest thing that happened in `rows` at or before `at`, and no earlier
 *  than `floor`. Rows and their steps run in time order, so the scan stops at
 *  the first one that starts after `at`. A turn request marker is the clock
 *  restarting, not activity. */
function lastActivityAtOrBefore(
  rows: ReadonlyArray<MessagesTimelineRow>,
  at: number,
  floor: number,
): number {
  let latest = floor;
  const consider = (time: string | null | undefined): number => {
    const parsed = time ? Date.parse(time) : Number.NaN;
    if (parsed > latest && parsed <= at) {
      latest = parsed;
    }
    return parsed;
  };
  for (const row of rows) {
    if (row.kind === "work") {
      for (const entry of row.groupedEntries) {
        if (entry.providerLifecyclePhase) {
          continue;
        }
        if (consider(entry.createdAt) > at) {
          return latest;
        }
        consider(entry.completedAt);
      }
      continue;
    }
    if (consider(row.kind === "message" ? row.message.createdAt : row.createdAt) > at) {
      return latest;
    }
    if (row.kind === "message") {
      consider(row.message.completedAt);
    }
  }
  return latest;
}

/** The index of the first time after `after` in ascending `times`. */
function firstIndexAfter(times: ReadonlyArray<number>, after: number): number {
  let low = 0;
  let high = times.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (times[middle]! > after) {
      high = middle;
    } else {
      low = middle + 1;
    }
  }
  return low;
}

/** What a turn's steps add up to: the files it edited, how its checks ended,
 *  and the agents its groups track. */
function summarizeTurnWork(
  rows: ReadonlyArray<MessagesTimelineRow>,
): Omit<TurnSummary, "workedMs"> {
  const editedFiles = new Set<string>();
  const checkResults = new Map<string, boolean>();
  const trackerTurnIds = new Set<TurnId>();
  const trackerAgentSpawnIds = new Set<string>();
  for (const row of rows) {
    if (row.kind !== "work") {
      continue;
    }
    row.trackerTurnIds.forEach((turnId) => trackerTurnIds.add(turnId));
    row.trackerAgentSpawnIds.forEach((spawnId) => trackerAgentSpawnIds.add(spawnId));
    for (const entry of row.groupedEntries) {
      if (entry.executionState !== "failed") {
        for (const path of entry.changedFiles ?? []) {
          editedFiles.add(path.replaceAll("\\", "/").toLowerCase());
        }
      }
      // A rerun of the same check replaces the earlier result. A check that
      // was blocked never ran, so it has no result.
      const checkKey =
        entry.command && entry.executionState !== "running" && !entry.blocked
          ? commandCheckKey(entry.command)
          : null;
      if (checkKey) {
        checkResults.set(checkKey, entry.executionState !== "failed");
      }
    }
  }
  const results = [...checkResults.values()];
  return {
    editedFileCount: editedFiles.size,
    checks:
      results.length > 0
        ? {
            passed: results.filter((passed) => passed).length,
            failed: results.filter((passed) => !passed).length,
          }
        : null,
    trackerTurnIds: [...trackerTurnIds],
    trackerAgentSpawnIds: [...trackerAgentSpawnIds],
  };
}

/** Labels the lifecycle status may replace on the working anchor. Anything
 *  else — "Waiting for approval", "Reverting checkpoint", "Thinking" — names a
 *  more specific state and keeps priority. */
const GENERIC_ANCHOR_LABELS: ReadonlySet<string> = new Set([
  "Working",
  "Preparing turn",
  "Sending",
]);

const LIFECYCLE_ANCHOR_LABELS = {
  preparing: "Preparing turn",
  "waiting-for-model": "Waiting for model",
  // Dynamic: the entry carries the provider's own wording; this is the fallback.
  "provider-status": "Waiting for model",
} as const satisfies Record<NonNullable<WorkLogEntry["providerLifecyclePhase"]>, string>;

/**
 * Provider lifecycle entries render no rows of their own; the working anchor
 * is the one place connection state is narrated. The visibility pass has
 * already dropped stale lifecycle entries (anything superseded by later
 * concrete activity or a newer phase), so any one still present names the
 * connection's actual current state.
 */
function resolveWorkingAnchorLabel(
  visibleTimelineEntries: ReadonlyArray<TimelineEntry>,
  activeStatusLabel: string | undefined,
): string {
  const fallback = activeStatusLabel ?? "Working";
  if (!GENERIC_ANCHOR_LABELS.has(fallback)) {
    return fallback;
  }
  for (let index = visibleTimelineEntries.length - 1; index >= 0; index -= 1) {
    const timelineEntry = visibleTimelineEntries[index];
    if (timelineEntry?.kind !== "work") {
      continue;
    }
    const phase = timelineEntry.entry.providerLifecyclePhase;
    if (phase) {
      return timelineEntry.entry.providerLifecycleLabel ?? LIFECYCLE_ANCHOR_LABELS[phase];
    }
  }
  return fallback;
}

/**
 * What the working row says, and the thought under it: a specific state when
 * there is one ("Waiting for approval", "Thinking"), else the step running
 * right now in the active exchange ("Reading service.ts"), else "Working".
 * While the agent's one running step is an ask or review another agent is
 * still answering, the row says what that agent is doing instead (`narrates`
 * names that answer): the call itself has nothing new to say until the
 * answer comes back.
 */
function resolveLiveAnchor(
  rows: ReadonlyArray<MessagesTimelineRow>,
  visibleTimelineEntries: ReadonlyArray<TimelineEntry>,
  input: Pick<
    Parameters<typeof deriveMessagesTimelineRows>[0],
    "activeStatusLabel" | "roomAgentNames" | "sideAnswers" | "openAgentRequestByMessageId"
  >,
): {
  readonly label: string;
  readonly thought: string | null;
  readonly narrates: SideTurnId | null;
} {
  const { roomAgentNames } = input;
  const thought = resolveLiveThought(visibleTimelineEntries);
  const label = resolveWorkingAnchorLabel(visibleTimelineEntries, input.activeStatusLabel);
  if (label !== "Working") {
    return { label, thought, narrates: null };
  }
  const lastUserIndex = rows.findLastIndex(
    (row) => row.kind === "message" && startsExchange(row.message),
  );
  const running = rows.slice(lastUserIndex + 1).flatMap((row) =>
    row.kind === "work"
      ? row.groupedEntries.flatMap((entry) => {
          if (entry.executionState !== "running") return [];
          const step = activityStepFromWorkLogEntry(entry, { roomAgentNames });
          return step ? [{ entry, step }] : [];
        })
      : [],
  );
  const waitedOn =
    running.length === 1
      ? sideAnswerAwaitedBy(running[0]!.entry, input.sideAnswers, input.openAgentRequestByMessageId)
      : null;
  const narrated = waitedOn ? narrateSideAnswer(waitedOn, roomAgentNames) : null;
  if (waitedOn && narrated) {
    return { ...narrated, narrates: waitedOn.sideTurnId };
  }
  return {
    label: liveActivityLabel(running.map(({ step }) => step)) ?? label,
    thought,
    narrates: null,
  };
}

/**
 * The side answer a running `room_ask` or `room_review` call waits on: one
 * an agent asked for, of the call's kind, still being written, whose call
 * has not stopped waiting for it. One side answer runs at a time, so there
 * is at most one; one a call stopped waiting for can still be running when
 * the agent makes its next call, which is not waiting on it.
 */
function sideAnswerAwaitedBy(
  call: WorkLogEntry,
  sideAnswers: ReadonlyArray<SideAnswerView> | undefined,
  openAgentRequestByMessageId: ReadonlyMap<MessageId, OrchestrationAgentRequest> | undefined,
): SideAnswerView | null {
  const tool = threadlinesRoomToolOf(call);
  if (tool !== "room_ask" && tool !== "room_review") {
    return null;
  }
  const kind = tool === "room_review" ? "review" : "ask";
  return (
    sideAnswers?.find(
      (view) =>
        view.askedBy !== null &&
        view.kind === kind &&
        (view.state === "answering" || view.state === "stopping") &&
        openAgentRequestByMessageId?.get(view.question.id)?.replyAsMessage !== true,
    ) ?? null
  );
}

/**
 * A side answer in the working row's words: the agent's name and the step it
 * is running ("GPT-6.1-Sol: Reading service.ts"), else that it is still at it
 * ("GPT-6.1-Sol is reviewing"), with the newest words of a thought it is
 * having. Null when the room has no name for the agent.
 */
function narrateSideAnswer(
  view: SideAnswerView,
  roomAgentNames: RoomAgentNames | null | undefined,
): { readonly label: string; readonly thought: string | null } | null {
  const name = roomAgentNames?.get(roomAgentKey(view.participantId));
  if (name === undefined) {
    return null;
  }
  const { step, thought } = sideAnswerLiveWork(view, roomAgentNames);
  return {
    label:
      view.state === "stopping"
        ? `${name} is stopping`
        : step !== null
          ? `${name}: ${step}`
          : `${name} is ${view.kind === "review" ? "reviewing" : "answering"}`,
    thought,
  };
}

/** The newest words of the thought running right now, when the newest step in
 *  view is one. A step or reply after it means the thought is over. */
function resolveLiveThought(visibleTimelineEntries: ReadonlyArray<TimelineEntry>): string | null {
  const newest = visibleTimelineEntries.findLast(
    (timelineEntry) => timelineEntry.kind !== "work" || !timelineEntry.entry.providerLifecyclePhase,
  );
  return newest?.kind === "work" ? liveThoughtText(newest.entry) : null;
}

/** The newest command still running in the exchange in view. A command the
 *  agent started alongside others keeps running after a quicker one ends. */
function resolveLiveCommand(
  visibleTimelineEntries: ReadonlyArray<TimelineEntry>,
): LiveCommandOutput | null {
  for (let index = visibleTimelineEntries.length - 1; index >= 0; index -= 1) {
    const timelineEntry = visibleTimelineEntries[index]!;
    if (timelineEntry.kind === "message" && startsExchange(timelineEntry.message)) return null;
    if (timelineEntry.kind !== "work") continue;
    const { entry } = timelineEntry;
    if (entry.itemType === "command_execution" && entry.executionState === "running") {
      return { toolCallId: entry.toolCallId ?? null, streamedOutput: entry.outputPreview ?? null };
    }
  }
  return null;
}

/**
 * A spawned agent's own tool calls are not the conversation's activity, so they
 * never reach the chat: not as expanded rows, and not in the receipt's counts.
 * The turn's tracker row says how many agents ran and the rail's Agents tab owns
 * what each of them did. The entries themselves are untouched — every other
 * reader of the work log still sees them.
 */
function isSubagentAttributedEntry(entry: TimelineEntry): boolean {
  return entry.kind === "work" && entry.entry.sourceAgentThreadId !== undefined;
}

/**
 * Agent lifecycle plumbing: the spawn/poll/close tool calls the main model makes
 * to run an agent, and the provider's own task stream for one. None of it is
 * work the conversation should narrate — the turn's tracker row says how many
 * agents ran, each finished agent files exactly one receipt, and the Agents tab
 * owns the detail. So the entries never render and never count, but they are not
 * discarded either: they stay on the row as its anchor, because a turn that only
 * delegated still has to carry that tracker.
 *
 * Both signals are payload-level, not label text: `collab_agent_tool_call` is
 * the item type every provider's agent tool call projects under (Codex's
 * `spawnAgent`/`wait`/`sendInput`/`closeAgent`, Claude's `Agent`/`Task`), and a
 * task activity is only an agent's when it carries that agent's identity —
 * Claude's background bash tasks share the activity kinds and stay.
 */
function isAgentLifecycleEntry(entry: TimelineEntry): boolean {
  if (entry.kind !== "work") {
    return false;
  }
  const { itemType, activityKind, subagentTask } = entry.entry;
  if (itemType === "collab_agent_tool_call") {
    return true;
  }
  return (
    (activityKind === "task.progress" || activityKind === "task.completed") &&
    subagentTask !== undefined
  );
}

/** The spawn call ids a turnless group's rows reference: an agent's own task
 *  rows name their spawn through `subagentTask.toolUseId`, and the spawn's
 *  collab tool item names itself through `toolCallId`. */
function deriveTrackerAgentSpawnIds(
  groupedEntries: ReadonlyArray<WorkLogEntry>,
  agentAnchorEntries: ReadonlyArray<WorkLogEntry>,
): string[] {
  const spawnIds: string[] = [];
  for (const entry of [...groupedEntries, ...agentAnchorEntries]) {
    const spawnId =
      entry.subagentTask?.toolUseId ??
      (entry.itemType === "collab_agent_tool_call" ? entry.toolCallId : undefined);
    if (spawnId && !spawnIds.includes(spawnId)) {
      spawnIds.push(spawnId);
    }
  }
  return spawnIds;
}

/**
 * A turn's response reads as its final word, and what the turn made for the
 * reader sits with it. Left in recorded order, two things break that:
 *
 * - Turn-end bookkeeping (the checkpoint's changed-files activity, a tool
 *   event that settles late) is recorded after the assistant message and
 *   would render below it.
 * - A page stands where the agent showed it, which is among its steps when
 *   it kept working afterwards.
 *
 * For every settled turn, work entries trailing the turn's last assistant
 * message move to just above that message, and the turn's pages move there
 * after them: the steps, then the pages, then the reply. A settled page
 * stands at its reply in time as well, so what the timeline places by time (a
 * side exchange) falls where it was asked, above the pair, never between a
 * page and its reply.
 *
 * The turn named by `activeTurnId` keeps raw order. While it is still
 * working, activity that starts after a streamed commentary segment really
 * is the newest thing and belongs below it, a page shows where it was
 * published, and no message can be called the reply yet. A page also stays
 * put while its turn's last message is still streaming, whatever the turn is
 * called.
 */
function settleTurnsAboveResponse(
  entries: TimelineEntry[],
  activeTurnId: TurnId | null,
): TimelineEntry[] {
  const lastAssistantIndexByTurn = new Map<TurnId, number>();
  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index]!;
    if (entry.kind !== "message" || entry.message.role !== "assistant") {
      continue;
    }
    const turnId = entry.message.turnId;
    if (turnId != null && turnId !== activeTurnId) {
      lastAssistantIndexByTurn.set(turnId, index);
    }
  }
  if (lastAssistantIndexByTurn.size === 0) {
    return entries;
  }

  const trailingWorkByAnchorIndex = new Map<number, TimelineEntry[]>();
  const pagesByAnchorIndex = new Map<number, TimelineEntry[]>();
  const movedIndices = new Set<number>();
  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index]!;
    const turnId =
      entry.kind === "work"
        ? entry.entry.turnId
        : entry.kind === "page"
          ? entry.page.turnId
          : entry.kind === "command-request"
            ? entry.request.turnId
            : null;
    if (turnId == null) {
      continue;
    }
    const anchorIndex = lastAssistantIndexByTurn.get(turnId);
    if (anchorIndex === undefined) {
      continue;
    }
    // Pages and command cards are part of the answer: they go right above it.
    if (entry.kind !== "page" && entry.kind !== "command-request") {
      if (index > anchorIndex) {
        const trailing = trailingWorkByAnchorIndex.get(anchorIndex) ?? [];
        trailing.push(entry);
        trailingWorkByAnchorIndex.set(anchorIndex, trailing);
        movedIndices.add(index);
      }
      continue;
    }
    const reply = entries[anchorIndex]!;
    if (reply.kind === "message" && reply.message.streaming) {
      continue;
    }
    const pages = pagesByAnchorIndex.get(anchorIndex) ?? [];
    pages.push(
      entry.createdAt === reply.createdAt ? entry : { ...entry, createdAt: reply.createdAt },
    );
    pagesByAnchorIndex.set(anchorIndex, pages);
    movedIndices.add(index);
  }
  if (movedIndices.size === 0) {
    return entries;
  }

  const result: TimelineEntry[] = [];
  for (let index = 0; index < entries.length; index += 1) {
    if (movedIndices.has(index)) {
      continue;
    }
    result.push(
      ...(trailingWorkByAnchorIndex.get(index) ?? []),
      ...(pagesByAnchorIndex.get(index) ?? []),
      entries[index]!,
    );
  }
  // A page already at its reply, in place and in time, moved nowhere.
  return result.every((entry, index) => entry === entries[index]) ? entries : result;
}

function deriveVisibleTimelineEntries(input: {
  readonly timelineEntries: ReadonlyArray<TimelineEntry>;
  readonly isWorking: boolean;
  readonly activeTurnId?: TurnId | null;
}): TimelineEntry[] {
  // Agent lifecycle entries stay in this pass: they still count as concrete turn
  // activity for the provider-lifecycle row's own visibility, and the grouping
  // step below is what parks them out of sight. Live agent commentary leaves
  // here entirely: it renders nothing, and its timestamp moves with every
  // streamed update — left in, it would split and re-merge the turn's work
  // groups as the agent streams, flickering the activity receipt in and out.
  const timelineEntries = input.timelineEntries.filter(
    (entry) => !isSubagentAttributedEntry(entry) && entry.kind !== "subagent-live",
  );
  const visibleByIndex = Array.from({ length: timelineEntries.length }, () => true);
  let hasLaterProviderLifecycle = false;
  let hasLaterConcreteTurnActivity = false;
  const laterConcreteTurnIds = new Set<TurnId>();

  for (let index = timelineEntries.length - 1; index >= 0; index -= 1) {
    const timelineEntry = timelineEntries[index];
    if (!timelineEntry) {
      continue;
    }

    if (timelineEntry.kind === "work" && timelineEntry.entry.providerLifecyclePhase) {
      visibleByIndex[index] = shouldShowProviderLifecycleWorkEntry(timelineEntry.entry, input, {
        hasLaterProviderLifecycle,
        hasLaterConcreteTurnActivity,
        laterConcreteTurnIds,
      });
    }

    if (timelineEntry.kind === "work" && timelineEntry.entry.providerLifecyclePhase) {
      hasLaterProviderLifecycle = true;
    }

    const concreteTurnId = concreteTimelineEntryTurnId(timelineEntry);
    if (concreteTurnId !== undefined) {
      hasLaterConcreteTurnActivity = true;
      if (concreteTurnId !== null) {
        laterConcreteTurnIds.add(concreteTurnId);
      }
    }
  }

  return timelineEntries.filter((_, index) => visibleByIndex[index]);
}

function shouldShowProviderLifecycleWorkEntry(
  entry: WorkLogEntry,
  input: {
    readonly isWorking: boolean;
    readonly activeTurnId?: TurnId | null;
  },
  later: {
    readonly hasLaterProviderLifecycle: boolean;
    readonly hasLaterConcreteTurnActivity: boolean;
    readonly laterConcreteTurnIds: ReadonlySet<TurnId>;
  },
): boolean {
  if (!input.isWorking) {
    return false;
  }

  const activeTurnId = input.activeTurnId ?? null;
  if (entry.providerLifecyclePhase === "preparing") {
    return (
      !later.hasLaterProviderLifecycle && !hasLaterConcreteTurnActivityForTurn(later, activeTurnId)
    );
  }

  if (activeTurnId !== null && entry.turnId !== activeTurnId) {
    return false;
  }
  return !hasLaterConcreteTurnActivityForTurn(later, activeTurnId ?? entry.turnId ?? null);
}

function hasLaterConcreteTurnActivityForTurn(
  later: {
    readonly hasLaterConcreteTurnActivity: boolean;
    readonly laterConcreteTurnIds: ReadonlySet<TurnId>;
  },
  turnId: TurnId | null,
): boolean {
  if (turnId === null) {
    return later.hasLaterConcreteTurnActivity;
  }
  return later.laterConcreteTurnIds.has(turnId);
}

/** The turns of rows `start` through `end` (clamped to the list), in row
 *  order. The chat reads it to fetch older turns' steps as they come into view. */
export function timelineRowTurnIds(
  rows: ReadonlyArray<MessagesTimelineRow>,
  start: number,
  end: number,
): TurnId[] {
  const turnIds = new Set<TurnId>();
  for (let index = Math.max(0, start); index <= Math.min(rows.length - 1, end); index += 1) {
    const row = rows[index];
    const turnId = row ? timelineRowTurnId(row) : null;
    if (turnId) {
      turnIds.add(turnId);
    }
  }
  return [...turnIds];
}

function timelineRowTurnId(row: MessagesTimelineRow): TurnId | null | undefined {
  switch (row.kind) {
    case "message":
      return row.message.turnId;
    case "work":
      return row.groupedEntries[0]?.turnId;
    case "proposed-plan":
      return row.proposedPlan.turnId;
    case "subagent-result":
      return row.result.turnId;
    case "workflow-run":
      return row.run.turnId;
    case "mcp-app":
      return row.app.turnId;
    default:
      return null;
  }
}

function concreteTimelineEntryTurnId(entry: TimelineEntry): TurnId | null | undefined {
  if (entry.kind === "message") {
    return entry.message.role === "assistant" ? (entry.message.turnId ?? null) : undefined;
  }
  if (entry.kind === "work" && !entry.entry.providerLifecyclePhase) {
    return entry.entry.turnId ?? null;
  }
  if (entry.kind === "subagent-result") {
    return entry.result.turnId ?? null;
  }
  if (entry.kind === "workflow-run") {
    return entry.run.turnId;
  }
  if (entry.kind === "mcp-app") {
    return entry.app.turnId;
  }
  return undefined;
}

function markLatestLiveWorkRow(
  rows: MessagesTimelineRow[],
  activeTurnId: TurnId | null,
  activeTurnStartedAt: string | null,
): boolean {
  // The live work group must be the tail of the timeline. Once the assistant
  // emits a message (or any other row) after the work, that work is no longer
  // the current activity — it freezes in place while the working row at the
  // very bottom keeps carrying the turn's live node.
  const lastIndex = rows.length - 1;
  const lastRow = rows[lastIndex];
  if (!lastRow || lastRow.kind !== "work") {
    return false;
  }
  // An anchor-only group has no step to hang the live node on — it renders as the
  // turn's agent tracker, not as a spine — so the standalone working row still
  // has to carry the live node at the bottom.
  if (lastRow.groupedEntries.length === 0) {
    return false;
  }
  // Reasoning and other lifecycle entries arrive without a turn id, so a tail
  // work group that carries no turn association is still treated as the live
  // one rather than handed off to a detached working row.
  const isActiveTurnWork =
    workRowMatchesActiveTurn(lastRow, activeTurnId) ||
    (activeTurnId !== null && lastRow.groupedEntries.every((entry) => entry.turnId === undefined));
  if (!isActiveTurnWork) {
    return false;
  }
  rows[lastIndex] = { ...lastRow, isLive: true, liveStartedAt: activeTurnStartedAt };
  return true;
}

function workRowMatchesActiveTurn(
  row: Extract<MessagesTimelineRow, { kind: "work" }>,
  activeTurnId: TurnId | null,
): boolean {
  if (activeTurnId === null) {
    return true;
  }
  return row.groupedEntries.some((entry) => entry.turnId === activeTurnId);
}

/**
 * The rows with each unchanged row's previous object reused, and what they
 * share with every row (timelineRowsShared.ts); `previous` itself while
 * nothing changed.
 */
export function computeStableMessagesTimelineRows(
  rows: MessagesTimelineRow[],
  previous: StableMessagesTimelineRowsState,
): StableMessagesTimelineRowsState {
  const next = new Map<string, MessagesTimelineRow>();
  let anyChanged = rows.length !== previous.byId.size;

  const result = rows.map((row, index) => {
    const prevRow = previous.byId.get(row.id);
    const nextRow = prevRow && isRowUnchanged(prevRow, row) ? prevRow : row;
    next.set(row.id, nextRow);
    if (!anyChanged && previous.result[index] !== nextRow) {
      anyChanged = true;
    }
    return nextRow;
  });

  return anyChanged
    ? { byId: next, result, shared: deriveTimelineRowsShared(result, previous.shared) }
    : previous;
}

/** Shallow field comparison per row variant — avoids deep equality cost. */
function isRowUnchanged(a: MessagesTimelineRow, b: MessagesTimelineRow): boolean {
  if (
    a.kind !== b.kind ||
    a.id !== b.id ||
    a.padTop !== b.padTop ||
    !sameAgentLine(a.agentLine, b.agentLine)
  ) {
    return false;
  }

  switch (a.kind) {
    case "working": {
      const bw = b as typeof a;
      return (
        a.createdAt === bw.createdAt &&
        a.label === bw.label &&
        a.thought === bw.thought &&
        a.command?.toolCallId === bw.command?.toolCallId &&
        a.command?.streamedOutput === bw.command?.streamedOutput &&
        sameIds(a.trackerTurnIds, bw.trackerTurnIds) &&
        sameIds(a.trackerAgentSpawnIds, bw.trackerAgentSpawnIds)
      );
    }

    case "check-back":
      return a.checkBacks === (b as typeof a).checkBacks;

    case "proposed-plan":
      return a.proposedPlan === (b as typeof a).proposedPlan;

    case "subagent-result":
      return a.result === (b as typeof a).result;

    case "workflow-run":
      return a.run === (b as typeof a).run;

    case "mcp-app":
      return a.app.toolCallId === (b as typeof a).app.toolCallId;

    case "fork-context":
      return a.forkContext === (b as typeof a).forkContext;

    case "moved-in":
      return a.movedIn === (b as typeof a).movedIn;

    case "page":
      return a.page === (b as typeof a).page && a.updated === (b as typeof a).updated;

    case "command-request":
      return a.request === (b as typeof a).request;

    case "side-status": {
      const bs = b as typeof a;
      return (
        a.state === bs.state &&
        a.error === bs.error &&
        a.participantId === bs.participantId &&
        a.review === bs.review &&
        a.question === bs.question &&
        a.answerMessageId === bs.answerMessageId &&
        a.compact === bs.compact &&
        a.expanded === bs.expanded &&
        a.liveStep === bs.liveStep
      );
    }

    case "work": {
      const bw = b as typeof a;
      return (
        a.isLive === bw.isLive &&
        a.liveStartedAt === bw.liveStartedAt &&
        a.inActiveExchange === bw.inActiveExchange &&
        a.folded === bw.folded &&
        sameIds(a.trackerTurnIds, bw.trackerTurnIds) &&
        sameIds(a.trackerAgentSpawnIds, bw.trackerAgentSpawnIds) &&
        Equal.equals(a.groupedEntries, bw.groupedEntries) &&
        Equal.equals(a.agentAnchorEntries, bw.agentAnchorEntries)
      );
    }

    case "message": {
      const bm = b as typeof a;
      return (
        a.message === bm.message &&
        a.settledNote === bm.settledNote &&
        isTurnSummaryUnchanged(a.turnSummary, bm.turnSummary) &&
        a.showAssistantCopyButton === bm.showAssistantCopyButton &&
        a.assistantCopyStreaming === bm.assistantCopyStreaming &&
        a.assistantTurnInProgress === bm.assistantTurnInProgress &&
        a.assistantModelFallback === bm.assistantModelFallback &&
        a.assistantTurnDiffSummary === bm.assistantTurnDiffSummary &&
        a.revertTurnCount === bm.revertTurnCount &&
        a.sideReview === bm.sideReview &&
        a.sideReplyTo === bm.sideReplyTo
      );
    }
  }
}

function isTurnSummaryUnchanged(a: TurnSummary | null, b: TurnSummary | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return (
    a.workedMs === b.workedMs &&
    a.editedFileCount === b.editedFileCount &&
    a.checks?.passed === b.checks?.passed &&
    a.checks?.failed === b.checks?.failed &&
    sameIds(a.trackerTurnIds, b.trackerTurnIds) &&
    sameIds(a.trackerAgentSpawnIds, b.trackerAgentSpawnIds)
  );
}

function sameIds(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

const MAX_COLLAPSED_USER_MESSAGE_LINES = 8;
const MAX_COLLAPSED_USER_MESSAGE_LENGTH = 600;

/**
 * How a message row is drawn (TimelineRowContent): the agent's own answer, a
 * message from another thread or from an agent outside Threadlines, the
 * user's message, an agent's invite or side reply, or a message one room
 * agent wrote to another. Null draws nothing. Find in chat reads the same
 * choice to know what text the row shows.
 */
export type TimelineMessageView =
  | "assistant"
  | "thread-message"
  | "outside-message"
  | "user"
  | "invite"
  | "side-answer-reply"
  | "agent-message";

export function timelineMessageView(
  message: Pick<
    ChatMessage,
    "role" | "fromThread" | "fromOutside" | "fromAgent" | "requestKind" | "requestId"
  >,
  sideAnswerRequests: { has(requestId: NonNullable<ChatMessage["requestId"]>): boolean },
): TimelineMessageView | null {
  if (message.role === "assistant") return "assistant";
  if (message.role !== "user") return null;
  if (message.fromThread !== undefined) return "thread-message";
  if (message.fromOutside !== undefined) return "outside-message";
  if (message.fromAgent === undefined) return "user";
  if (message.requestKind === "invite") return "invite";
  if (
    message.requestKind === "reply" &&
    message.requestId !== undefined &&
    sideAnswerRequests.has(message.requestId)
  ) {
    return "side-answer-reply";
  }
  return "agent-message";
}

/**
 * The message a scheduled task's run started its thread with: the thread's
 * first message of the user's own (not one another agent or thread sent).
 * Null when there is none yet.
 */
export function scheduledRunMessageId(
  timelineEntries: ReadonlyArray<TimelineEntry>,
): ChatMessage["id"] | null {
  for (const entry of timelineEntries) {
    if (entry.kind !== "message") continue;
    if (entry.message.role !== "user") continue;
    return entry.message.fromThread === undefined &&
      entry.message.fromAgent === undefined &&
      entry.message.fromOutside === undefined
      ? entry.message.id
      : null;
  }
  return null;
}

/** Longest one-line message agents send each other that shows whole, not folded. */
const AGENT_MESSAGE_UNFOLDED_MAX_LENGTH = 120;

/**
 * A message agents send each other shows folded to its first line, unless
 * that line is all of it (agentMessageFolds.ts).
 */
export function foldsAgentMessage(text: string): boolean {
  const trimmed = text.trim();
  return trimmed.includes("\n") || trimmed.length > AGENT_MESSAGE_UNFOLDED_MAX_LENGTH;
}

/** A long message of yours shows folded, behind a fade and a way to open it. */
export function shouldCollapseUserMessage(text: string): boolean {
  if (text.trim().length === 0) {
    return false;
  }
  return (
    text.length > MAX_COLLAPSED_USER_MESSAGE_LENGTH ||
    text.split("\n").length > MAX_COLLAPSED_USER_MESSAGE_LINES
  );
}

// How tall a row draws, guessed from its data, for the rows the list has not
// drawn yet. Left alone, the list guesses from the average of the rows it has
// measured: long answers make it guess high for a step or a message that just
// started streaming, which then shrinks as soon as it lands, and a phone
// scrolled to the bottom snaps back by the difference. The average also moves
// with every row measured, shifting every undrawn row above the reader. These
// guesses stay put, and follow how the rows render in MessagesTimeline.tsx
// closely enough; the list measures each row once it draws it.
const TEXT_LINE_PX = 23;
const TEXT_CHAR_PX = 6.5;
const STEP_LINE_PX = 20;

const rowHeightEstimates = new WeakMap<
  MessagesTimelineRow,
  { readonly width: number; readonly height: number }
>();

/** The moved-here boundary (MovedInTimelineRow): an 18px line and the row's 16px bottom padding. */
const MOVED_IN_ROW_ESTIMATED_PX = 34;

/** An agent page's panel around its frame (AgentPageRow): padding, and the
 *  title line above the frame with its margin. */
const AGENT_PAGE_PANEL_PX = { sides: 28, chrome: 50 } as const;

/** The guessed height of `row` in a timeline `width` pixels wide. */
export function estimateTimelineRowHeight(row: MessagesTimelineRow, width: number): number {
  const cached = rowHeightEstimates.get(row);
  if (cached?.width === width) {
    return cached.height;
  }
  const content = estimateRowContentHeight(row, width);
  // A row that draws nothing drops its padding too.
  const height = content > 0 && row.padTop ? content + 8 : content;
  rowHeightEstimates.set(row, { width, height });
  return height;
}

function estimateRowContentHeight(row: MessagesTimelineRow, width: number): number {
  // The list's side padding, then the rows' 56rem column and its own padding.
  const column = Math.min(width - (width < 640 ? 24 : 40), 896) - 16;
  switch (row.kind) {
    case "message": {
      if (row.message.role === "assistant") {
        const hasChangedFiles =
          !row.assistantTurnInProgress && (row.assistantTurnDiffSummary?.files.length ?? 0) > 0;
        return (
          8 +
          // A side answer's author line, and its link back when posted below.
          (row.message.sideTurnId !== undefined ? 20 : 0) +
          estimateTextLines(row.message.text, column - 8) * TEXT_LINE_PX +
          (row.turnSummary ? 38 : 0) +
          // The turn's changes, a card whose header wraps on a phone.
          (hasChangedFiles ? (column < 430 ? 110 : 88) : 0)
        );
      }
      if (
        row.message.role === "user" &&
        (row.message.fromAgent !== undefined ||
          row.message.fromThread !== undefined ||
          row.message.fromOutside !== undefined)
      ) {
        // An agent's message to another: a meta line over its text, no bubble,
        // or over its first line while it shows folded.
        return foldsAgentMessage(row.message.text)
          ? 28 + TEXT_LINE_PX
          : 28 + estimateTextLines(row.message.text, column - 24) * TEXT_LINE_PX;
      }
      if (row.message.role === "user") {
        const text = deriveDisplayedUserMessageState(row.message.text).visibleText;
        const images = row.message.attachments?.some((attachment) => attachment.type === "image")
          ? 228
          : 0;
        if (shouldCollapseUserMessage(text)) {
          return 252 + images;
        }
        // The bubble takes 80% of the column, less its padding.
        return 78 + estimateTextLines(text, column * 0.8 - 34) * TEXT_LINE_PX + images;
      }
      return 0;
    }
    case "work": {
      // A running step shows on the working row, not here. A folded stretch
      // is one line. The one the agent is on shows its newest steps, each on a
      // line, under one line for whatever came before them; a finished thought
      // never takes a line of its own.
      let steps = 0;
      let thoughts = 0;
      for (const entry of row.groupedEntries) {
        if (entry.executionState === "running" || isSilentWorkLogEntry(entry)) {
          continue;
        }
        if (entry.tone === "thinking") {
          thoughts += 1;
        } else {
          steps += 1;
        }
      }
      if (steps + thoughts === 0) {
        return 0;
      }
      const lines = row.folded
        ? 1
        : Math.min(steps, OPEN_TAIL_LINES) + (steps > OPEN_TAIL_LINES || thoughts > 0 ? 1 : 0);
      return 10 + STEP_LINE_PX * lines;
    }
    case "working":
      return row.thought || row.command ? 46 : 30;
    case "check-back":
      return 46;
    case "subagent-result":
      return 40;
    case "workflow-run":
      return estimateWorkflowRunCardHeight(row.run);
    case "mcp-app":
      // Its title line and the app at its starting height (McpAppCard).
      return MCP_APP_CARD_ESTIMATED_PX;
    case "fork-context":
      return 90;
    case "moved-in":
      return MOVED_IN_ROW_ESTIMATED_PX;
    case "proposed-plan":
      return 240;
    case "page":
      // Its panel and title line, then the page at the height measured for
      // the frame's width: the column less the agent's lane and the panel.
      return (
        AGENT_PAGE_PANEL_PX.chrome +
        agentPageFrameHeight(row.page, column - 20 - AGENT_PAGE_PANEL_PX.sides)
      );
    case "command-request":
      // Header, reason, the command's lines, the folder line and the buttons.
      return 150 + 20 * Math.min(row.request.command.split("\n").length, 12);
    case "side-status":
      return 30;
  }
}

/** Rendered lines of `text` in a box `widthPx` wide, a blank line counting
 *  half. A line breaks at a word, losing about half of one. */
function estimateTextLines(text: string, widthPx: number): number {
  if (text.length === 0) {
    return 0;
  }
  const charsPerLine = Math.max(1, widthPx / TEXT_CHAR_PX - 3);
  let lines = 0;
  for (const line of text.split("\n")) {
    lines += line.trim().length === 0 ? 0.5 : Math.ceil(line.length / charsPerLine);
  }
  return lines;
}
